// @flow
import { useEffect, useRef, useState } from 'react';
//Components
import { Link } from 'react-router-dom';
import { ItemListImage, useScreenWidth } from '../../common';
import AddAfterExistsButton from '../../common/components/AddAfterExistsButton';
import InputFieldErrorMessage from '../../common/components/InputFieldErrorMessage';
import PriceInformationNotice from '../../common/components/PriceInformationNotice';
import AddToCartButton from '../../current-order/containers/AddToCartButton';
import GiftCardItem from './GiftCardItem';
import ItemOptionSwitch from './ItemOptionSwitch';
import LoadingBar from './LoadingBar';
import PortionSlider, {
  getPortionLimits,
  isItemPortioned,
} from './PortionSlider';
// Actions
import { changeLineItem } from '../actions';
//Selector
import {
  changingItemLoadingSelector,
  editingItemLoadingByIdSelector,
} from '../selectors';
// Composers
import { connect } from 'react-redux';
// Hooks
import useObservationAutosave from '../customHooks/useObservationAutosave';
// Assets
import QuestionMarkIcon from '../images/question-mark.svg';
//Style
import 'rc-tooltip/assets/bootstrap.css';
import './Item.css';
// Types
import type { ObservationSaveStatus } from '../customHooks/useObservationAutosave';
import type { LineItem } from '../types';

type OwnProps = {
  item: LineItem,
  productId: number,
  error: string[],
  loading?: boolean,
  handleRemoveItem: (lineItemId: number) => void,
};

type Props = OwnProps & {
  updatingItemLoading: boolean,
  changingLoading: boolean,
  handleChangePortion: (
    observation: string,
    portioningSize: ?number,
  ) => Promise<boolean>,
};

type ObservationStatusProps = {
  observation: string,
  status: ObservationSaveStatus,
  onRetry: () => void,
  showObservation?: boolean,
};

const ObservationStatus = ({
  observation,
  status,
  onRetry,
  showObservation,
}: ObservationStatusProps) => {
  if (status === 'pending') {
    return (
      <p className="line-item-observation-status">
        <i className="fa fa-spinner fa-pulse" aria-hidden="true" /> Salvando...
      </p>
    );
  }
  if (status === 'error') {
    return (
      <p className="line-item-observation-status error">
        Não foi possível salvar.{' '}
        <button type="button" onClick={onRetry}>
          Tentar novamente
        </button>
      </p>
    );
  }
  if (status === 'saved' && observation) {
    return (
      <p className="line-item-observation-status saved">
        <i className="fa fa-check" aria-hidden="true" /> Observação salva
        {showObservation && `: ${observation}`}
      </p>
    );
  }
  return null;
};

const Item = ({
  item,
  error,
  loading,
  updatingItemLoading,
  changingLoading,
  handleRemoveItem,
  handleChangePortion,
}: Props) => {
  const windowWidth = useScreenWidth();
  const [isPortioned, setIsPortioned] = useState(isItemPortioned(item));
  const [portioningSize, setPortioningSize] = useState(
    item.portioningSize || null,
  );
  const [sliderMaxQuantity, setSliderMaxQuantity] = useState(item.quantity);

  const {
    observation,
    isObservationOpen,
    badObservation,
    observationStatus,
    handleChangeObservation,
    handleToggleObservation,
    saveObservation,
    retryObservationSave,
  } = useObservationAutosave(item.observation, text =>
    handleChangePortion(text, portioningSize),
  );

  const savingPortionsRef = useRef(0);
  const savePortion = async (size: number) => {
    setPortioningSize(size);
    savingPortionsRef.current += 1;
    try {
      await handleChangePortion(observation, size);
    } finally {
      savingPortionsRef.current -= 1;
    }
  };

  // Keeps the slider in sync when the API returns a different portion
  const previousItemPortioningSizeRef = useRef(item.portioningSize);
  useEffect(() => {
    if (previousItemPortioningSizeRef.current === item.portioningSize) return;
    previousItemPortioningSizeRef.current = item.portioningSize;
    if (savingPortionsRef.current > 0) return;

    if (item.portioningSize !== portioningSize) {
      setIsPortioned(isItemPortioned(item));
      setPortioningSize(item.portioningSize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.portioningSize]);

  const handleTogglePortioning = (value: boolean) => {
    setIsPortioned(value);
    savePortion(value ? getPortionLimits(item, sliderMaxQuantity)[0] : 0);
  };

  const isGiftCard = item?.giftCard?.id;

  const isFreebie = !!item?.isFreebie;

  const discountPercentage = Math.round(
    ((item.variant.listPrice - item.variant.price) / item.variant.listPrice) *
      100,
  );

  if (isGiftCard) return <GiftCardItem item={item} error={error} />;
  return (
    <div>
      <div className="item-list-main-container">
        <div className="item-list-image-container">
          <section
            className={`search-result-square-image-section-cart${isFreebie ? ' freebie' : ''}`}
          >
            {discountPercentage >= 5 && (
              <div className="product-square-image-section-discount-cart">
                <p>{Math.round(discountPercentage)}%</p>
                <span>OFF</span>
              </div>
            )}
            <ItemListImage variant={item.variant} disableLink={isFreebie} />
          </section>
          {windowWidth < 768 && (
            <div className="item-list-header">
              <div className="line-item-content">
                <p>{item.variant.manufacturer}</p>
                {isFreebie ? (
                  <h5 className="line-item-name-text">{item.variant.name}</h5>
                ) : (
                  <Link to={`/produtos/${item.variant.slug}/`}>
                    <h5 className="line-item-name-text">{item.variant.name}</h5>
                  </Link>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="item-list-content">
          {windowWidth > 768 && (
            <div className="item-list-header">
              <div className="line-item-content">
                <p>{item.variant.manufacturer}</p>
                {isFreebie ? (
                  <h5 className="line-item-name-text">{item.variant.name}</h5>
                ) : (
                  <Link to={`/produtos/${item.variant.slug}/`}>
                    <h5 className="line-item-name-text">{item.variant.name}</h5>
                  </Link>
                )}
              </div>
            </div>
          )}
          {!isFreebie && (
            <div className="line-item-options">
              <div
                className="line-item-option-switches"
                role="group"
                aria-label="Opções do item"
              >
                {item.variant.unitForQuantity === 'g' && (
                  <ItemOptionSwitch
                    label="Porcionar produto"
                    icon="fa-cube"
                    info="Separamos o produto em embalagens do tamanho que você escolher. Ex.: 2 kg em porções de 500 g viram 4 embalagens."
                    checked={isPortioned}
                    onChange={handleTogglePortioning}
                  />
                )}
                <ItemOptionSwitch
                  label="Observação"
                  icon="fa-pencil"
                  info="Deixe um recado para a equipe que prepara seu pedido, como a espessura do corte."
                  checked={isObservationOpen}
                  onChange={handleToggleObservation}
                />
              </div>
              {!isObservationOpen &&
                (!!observation || observationStatus === 'error') && (
                  <div
                    className="line-item-observation-status-container line-item-observation-summary"
                    aria-live="polite"
                  >
                    <ObservationStatus
                      observation={observation}
                      status={observationStatus}
                      onRetry={retryObservationSave}
                      showObservation
                    />
                  </div>
                )}
              {item.variant.unitForQuantity === 'g' && isPortioned && (
                <PortionSlider
                  portion={portioningSize}
                  item={item}
                  onCommit={savePortion}
                  itemQuantity={sliderMaxQuantity}
                />
              )}
              {isObservationOpen && (
                <div className="line-item-observation-main">
                  <form>
                    <InputFieldErrorMessage message={badObservation} />
                    <textarea
                      aria-label="Observação para a produção"
                      placeholder="Observações para a produção"
                      value={observation}
                      onChange={handleChangeObservation}
                      onBlur={saveObservation}
                    ></textarea>
                  </form>
                  <div
                    className="line-item-observation-status-container"
                    aria-live="polite"
                  >
                    <ObservationStatus
                      observation={observation}
                      status={observationStatus}
                      onRetry={retryObservationSave}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="item-list-price-container">
            <div className="line-item-price-content">
              {!isFreebie && (
                <div className="line-item-price-container-option">
                  <button
                    className="item-list-trash-button"
                    aria-hidden="true"
                    onClick={() => handleRemoveItem(item.id)}
                    disabled={updatingItemLoading}
                  >
                    Excluir
                  </button>
                </div>
              )}
              {!isFreebie ? (
                <div className="line-item-add-to-cart-button-container">
                  <AddToCartButton
                    productId={item.variant.id}
                    unitForQuantity={item.variant.unitForQuantity}
                    defaultQuantity={item.quantity}
                    incrementQuantity={item.variant.incrementQuantity}
                    inStock={item.variant.inStock}
                    discontinued={item.variant.discontinued}
                    disableInsteadOfDeleteOnMinimumQuantity={true}
                    observation={observation}
                    analyticsEventType={'cart'} // USE ANALYTICS
                    portionQuantity={portioningSize}
                    setPortionQuantity={setPortioningSize}
                    setSliderMaxQuantity={setSliderMaxQuantity}
                  />
                  {item.variant.unitForQuantity === 'g' && (
                    <p className="line-item-min-quantity">
                      Quantidade mínima: &nbsp;
                      {(item.variant.minimumQuantity / 1000).toLocaleString(
                        'pt-br',
                      )}{' '}
                      Kg
                    </p>
                  )}
                  {error?.length > 0 && (
                    <div className="add-to-cart-error">
                      <InputFieldErrorMessage message={error[0]} />
                    </div>
                  )}
                </div>
              ) : (
                <AddAfterExistsButton
                  unitForQuantity={item.variant.unitForQuantity}
                  currentQuantity={item.quantity}
                  disableDecreaseQuantity={true}
                  disableIncrementQuantity={true}
                />
              )}
              {changingLoading && (
                <div className="item-changing-loading-container">
                  <i className="fa fa-spinner fa-pulse fa-1x fa-fw cart-indicator-spinner" />
                </div>
              )}
            </div>
            {!isFreebie ? (
              <div className="line-item-estimated-price">
                <p className="line-item-estimated-price-title">
                  Preço Estimado
                </p>
                <div className="line-item-understand-the-price">
                  <img src={QuestionMarkIcon} alt="entenda o preco" />
                  <span>Entenda o preço</span>
                  <div className="line-item-price-information-notice">
                    <PriceInformationNotice />
                  </div>
                </div>
                <div className="line-item-estimated-price-values">
                  <h6 className="line-item-product-total-price">
                    {parseFloat(
                      item.variant.price * item.quantity,
                    ).toLocaleString('pt-br', {
                      style: 'currency',
                      currency: 'BRL',
                    })}
                  </h6>
                  <h6 className="line-item-product-price">
                    {parseFloat(
                      item.variant.unitForQuantity === 'g'
                        ? item.variant.price * 1000
                        : item.variant.price,
                    ).toLocaleString('pt-br', {
                      style: 'currency',
                      currency: 'BRL',
                    })}
                    <small>
                      /{item.variant.unitForQuantity === 'g' ? 'Kg' : 'Un'}
                    </small>
                  </h6>
                </div>
              </div>
            ) : (
              <div className="line-item-prices-container">
                <span className="freebie-label">BRINDE</span>
              </div>
            )}
          </div>
        </div>

        <LoadingBar isVisible={loading} />
      </div>
    </div>
  );
};

const mapStateToProps = (state, ownProps: OwnProps) => {
  return {
    updatingItemLoading: editingItemLoadingByIdSelector(
      state,
      ownProps.productId,
    ),
    changingLoading: changingItemLoadingSelector(state, ownProps.productId),
  };
};

const mapDispatchToProps = (dispatch: Dispatch, ownProps: OwnProps) => {
  return {
    handleChangePortion: (observation: string, portioningSize: ?number) =>
      dispatch(
        changeLineItem(
          ownProps.productId,
          observation,
          ownProps.item.quantity,
          portioningSize,
        ),
      ),
  };
};

const connector: Connector<{}, Props> = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default connector(Item);
